import { Fragment } from 'react';
import { Disclosure, Menu, Transition } from '@headlessui/react';
import { signOut } from 'next-auth/react';
import Link from 'next/link';
import { User } from '@prisma/client';
import { UserCircleIcon } from '@heroicons/react/20/solid';

const userNavigation = [{ name: 'Sign out', onClick: () => signOut() }];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

type Props = {
  status: 'unauthenticated' | 'authenticated' | 'loading';
  user?: User;
};

export function ProfileMenu({ status, user }: Props) {
  switch (status) {
    case 'unauthenticated':
      return (
        <Link
          href="/login"
          passHref
          className="px-3 py-2 rounded-md text-sm font-medium text-rum-200 hover:text-white"
        >
          Sign In
        </Link>
      );
    case 'authenticated':
      return (
        <Menu as="div" className="ml-4 relative flex-shrink-0">
          <div>
            <Menu.Button className="bg-rum-700 flex text-sm rounded-full text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-rum-700 focus:ring-white">
              <span className="sr-only">Open user menu</span>
              {user?.pictureUrl ? (
                <img
                  className="h-8 w-8 rounded-full"
                  src={user?.pictureUrl}
                  alt=""
                />
              ) : (
                <UserCircleIcon className="h-8 w-8" />
              )}
            </Menu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
              {userNavigation.map((item) => (
                <Menu.Item key={item.name}>
                  {({ active }) => (
                    <a
                      onClick={item.onClick}
                      className={classNames(
                        active ? 'bg-gray-100' : '',
                        'cursor-pointer block px-4 py-2 text-sm text-gray-700',
                      )}
                    >
                      {item.name}
                    </a>
                  )}
                </Menu.Item>
              ))}
            </Menu.Items>
          </Transition>
        </Menu>
      );
    default:
      return null;
  }
}

export function ProfileDisclosure({ status }: Props) {
  switch (status) {
    case 'unauthenticated':
      return (
        <Link href="/login" passHref legacyBehavior>
          <Disclosure.Button
            as="a"
            className="block px-3 py-2 rounded-md text-base font-medium text-rum-200 hover:text-rum-100 hover:bg-rum-600"
          >
            Sign In
          </Disclosure.Button>
        </Link>
      );
    case 'authenticated':
      return (
        <>
          {userNavigation.map((item) => {
            const Component = () => (
              <Disclosure.Button
                key={item.name}
                as="a"
                className="block px-3 py-2 rounded-md text-base font-medium text-rum-200 hover:text-rum-100 hover:bg-rum-600"
              >
                {item.name}
              </Disclosure.Button>
            );

            if (item.onClick) {
              return (
                <div
                  key={item.name}
                  className="cursor-pointer"
                  onClick={() => item.onClick()}
                >
                  <Component />
                </div>
              );
            }

            return <Component key={item.name} />;
          })}
        </>
      );
    default:
      return null;
  }
}
